import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { SocialMediaAsset, SocialPost, Talent } from "@shared/schema";
import { AIDraftButton } from "@/components/ai-draft-button";
import { AIImageGeneratorDialog } from "./ai-image-generator-dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, X } from "lucide-react";

const composerSchema = z.object({
  campaignTag: z.string().max(100).optional().or(z.literal("")),
  internalNote: z.string().max(8000).optional().or(z.literal("")),
  talentId: z.string().optional().or(z.literal("")),
  scheduledAt: z.string().optional().or(z.literal("")), // datetime-local string
  status: z.enum(["draft", "needs_approval", "scheduled"]).default("draft"),
});

type ComposerValues = z.infer<typeof composerSchema>;

interface PostComposerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post?: SocialPost | null; // edit mode if provided
}

export function PostComposerDialog({ open, onOpenChange, post }: PostComposerDialogProps) {
  const { toast } = useToast();
  const isEdit = !!post;

  const { data: talent } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: open,
  });

  // ── Wave 9 — AI image attachment ──────────────────────────────────
  // The post carries a `defaultMediaAssetIds` list that auto-attaches to
  // every per-platform target created later. The composer lets the user
  // generate AI thumbnails inline; in edit mode we PATCH the post
  // immediately, in create mode we batch the IDs into the create payload.
  const [aiOpen, setAiOpen] = useState(false);
  const [attachedAssets, setAttachedAssets] = useState<SocialMediaAsset[]>([]);

  // Hydrate the attached list from the post's persisted defaultMediaAssetIds
  // when the dialog opens. We fetch the asset rows so we can render previews.
  // For create mode we just start with an empty list.
  const persistedIds = (post?.defaultMediaAssetIds ?? []) as string[];
  const persistedKey = persistedIds.join(",");
  const persistedAssetsQuery = useQuery<SocialMediaAsset[]>({
    queryKey: ["/api/social/media/by-ids", { ids: persistedKey }],
    enabled: open && !!post && persistedIds.length > 0,
    queryFn: async () => {
      // Batch endpoint returns just the assets referenced by this post,
      // in the same order as the supplied ids — so workspaces with hundreds
      // of media items don't pay for a full library list just to render a
      // couple of attachment thumbnails.
      const res = await apiRequest("POST", "/api/social/media/by-ids", { ids: persistedIds });
      return (await res.json()) as SocialMediaAsset[];
    },
  });

  // Hydrate exactly once per (dialog open, post id) pair, and only after the
  // persisted-asset query has resolved (or there are no ids to fetch). This
  // prevents a late-resolving query from clobbering local edits the user
  // made between dialog-open and query-success.
  const hydrationKey = open ? `${post?.id ?? "new"}` : null;
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      hydratedKeyRef.current = null;
      return;
    }
    if (hydratedKeyRef.current === hydrationKey) return;
    if (!post) {
      setAttachedAssets([]);
      hydratedKeyRef.current = hydrationKey;
      return;
    }
    if (persistedIds.length === 0) {
      setAttachedAssets([]);
      hydratedKeyRef.current = hydrationKey;
      return;
    }
    if (persistedAssetsQuery.data) {
      setAttachedAssets(persistedAssetsQuery.data);
      hydratedKeyRef.current = hydrationKey;
    }
  }, [open, hydrationKey, post, persistedIds.length, persistedAssetsQuery.data]);
  // Quick policy probe so we can hide the "Generate with AI" button entirely
  // when the workspace has the feature off (saves the user a wasted click).
  const policy = useQuery<{ aiEnabled: boolean; policy: { imageEnabled: boolean; nanoBananaEnabled: boolean; runwayEnabled: boolean } }>({
    queryKey: ["/api/social/ai-images/policy"],
    enabled: open,
  });
  const aiAvailable = !!(
    policy.data?.aiEnabled &&
    policy.data?.policy.imageEnabled &&
    (policy.data.policy.nanoBananaEnabled || policy.data.policy.runwayEnabled)
  );

  const form = useForm<ComposerValues>({
    resolver: zodResolver(composerSchema),
    defaultValues: {
      campaignTag: "",
      internalNote: "",
      talentId: "",
      scheduledAt: "",
      status: "draft",
    },
  });

  // Reset form when post or open state changes
  useEffect(() => {
    if (open) {
      form.reset({
        campaignTag: post?.campaignTag ?? "",
        internalNote: post?.internalNote ?? "",
        talentId: post?.talentId ?? "",
        scheduledAt: post?.scheduledAt
          ? new Date(post.scheduledAt).toISOString().slice(0, 16)
          : "",
        status: (post?.status as any) ?? "draft",
      });
    }
  }, [open, post, form]);

  const submit = useMutation({
    mutationFn: async (values: ComposerValues) => {
      const payload: Record<string, any> = {
        campaignTag: values.campaignTag?.trim() || null,
        internalNote: values.internalNote?.trim() || null,
        talentId: values.talentId || null,
        scheduledAt: values.scheduledAt ? new Date(values.scheduledAt).toISOString() : null,
        status: values.status,
        // Persist the post-level default media list so future per-platform
        // targets created for this post auto-attach the same assets.
        defaultMediaAssetIds: attachedAssets.map((a) => a.id),
      };
      if (isEdit && post) {
        const res = await apiRequest("PATCH", `/api/social/posts/${post.id}`, payload);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/social/posts", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/posts"] });
      toast({
        title: isEdit ? "Post updated" : "Draft created",
        description: isEdit
          ? "Your changes have been saved."
          : "You can keep editing it from the Posts tab.",
      });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({
        title: isEdit ? "Could not update post" : "Could not create post",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Edit-mode helper: persist a new defaultMediaAssetIds list to the existing
  // post immediately so the change survives closing the dialog without a
  // full save. In create mode we just keep the assets in local state until
  // the user submits the form.
  const persistAttached = useMutation({
    mutationFn: async (assets: SocialMediaAsset[]) => {
      if (!post) return null;
      const res = await apiRequest("PATCH", `/api/social/posts/${post.id}`, {
        defaultMediaAssetIds: assets.map((a) => a.id),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/posts"] });
    },
    onError: (err: any) => {
      toast({
        title: "Could not save attachment",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleRemoveAttached = (id: string) => {
    setAttachedAssets((prev) => {
      const next = prev.filter((x) => x.id !== id);
      if (isEdit && post) persistAttached.mutate(next);
      return next;
    });
  };

  const handleAttachGenerated = (asset: SocialMediaAsset) => {
    setAttachedAssets((prev) => {
      if (prev.find((x) => x.id === asset.id)) return prev;
      const next = [...prev, asset];
      if (isEdit && post) persistAttached.mutate(next);
      return next;
    });
  };

  const onSubmit = (values: ComposerValues) => submit.mutate(values);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="dialog-post-composer">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit post" : "New social post"}</DialogTitle>
          <DialogDescription>
            Draft your caption, set a publish window, and we will hold it ready until live
            publishing is approved per platform.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="campaignTag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Campaign / label</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Summer Wedding Promo"
                      data-testid="input-campaign-tag"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Internal label so you can find this post later.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="internalNote"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Caption</FormLabel>
                    <AIDraftButton
                      kind="social_description"
                      context={`Existing caption:\n${field.value || "(empty)"}`}
                      onDraft={(text) => field.onChange(text)}
                    />
                  </div>
                  <FormControl>
                    <Textarea
                      rows={6}
                      placeholder="Write the post you want to share. Hashtags can go inline or in the first comment when we go live."
                      data-testid="input-caption"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="talentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Talent (optional)</FormLabel>
                    <Select value={field.value || "__none"} onValueChange={(v) => field.onChange(v === "__none" ? "" : v)}>
                      <FormControl>
                        <SelectTrigger data-testid="select-talent">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none">None</SelectItem>
                        {(talent || []).map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-status">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="needs_approval">Needs approval</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Wave 9 — AI image generator entry point. Hidden when the workspace
                has the feature off so the composer stays uncluttered. */}
            {aiAvailable && (
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4" /> Media (AI thumbnail)
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Generate a thumbnail and we will auto-attach it as the default media for every platform you add to this post.
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAiOpen(true)}
                    data-testid="button-open-ai-image-composer"
                  >
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    Generate with AI
                  </Button>
                </div>
                {attachedAssets.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="list-attached-media">
                    {attachedAssets.map((a) => (
                      <div
                        key={a.id}
                        className="relative w-20 h-20 rounded-md border bg-muted overflow-hidden group"
                        data-testid={`thumb-ai-${a.id}`}
                      >
                        {a.objectKey ? (
                          <img
                            src={a.objectKey.startsWith("/objects/") ? a.objectKey : `/objects/${a.objectKey}`}
                            alt={a.altText || a.fileName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground p-1 text-center">
                            {a.fileName}
                          </div>
                        )}
                        {a.aiGenerated && (
                          <Badge variant="secondary" className="absolute bottom-0.5 left-0.5 text-[9px] px-1 py-0">AI</Badge>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttached(a.id)}
                          disabled={persistAttached.isPending}
                          className="absolute top-0.5 right-0.5 bg-background/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          aria-label="Remove attached thumbnail"
                          data-testid={`button-remove-ai-${a.id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="scheduledAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scheduled date &amp; time (optional)</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" data-testid="input-scheduled-at" {...field} />
                  </FormControl>
                  <FormDescription>
                    Times are stored in UTC. Live publishing turns on per platform after each platform's
                    review is approved.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel-post"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submit.isPending}
                data-testid="button-save-post"
              >
                {submit.isPending ? "Saving..." : isEdit ? "Save changes" : "Create draft"}
              </Button>
            </DialogFooter>
          </form>
        </Form>

        <AIImageGeneratorDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          attachMode
          initialPrompt={form.getValues("internalNote") || form.getValues("campaignTag") || ""}
          onSaved={handleAttachGenerated}
        />
      </DialogContent>
    </Dialog>
  );
}
